import { LEVEL_BADGE_CLASSES, LEVEL_LABELS } from "@/lib/level-badge";
import { cn } from "@/lib/utils";
import { scoreRisk } from "@shared/scoring/risk-matrix";
import type { Score } from "@shared/scoring/types";

const SCORES: Score[] = [1, 2, 3, 4];
/** Exposure runs bottom-to-top, so the worst combination sits top-right. */
const EXPOSURE_ROWS: Score[] = [4, 3, 2, 1];

/** Every cell calls scoreRisk — the matrix on screen is the matrix in use. */
export function RiskMatrixGrid() {
  return (
    <div className="flex flex-col gap-4 p-5">
      <div className="flex gap-2">
        <div className="flex items-center">
          <span className="whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground [writing-mode:vertical-rl] [transform:rotate(180deg)]">
            Exposure 1–4 →
          </span>
        </div>

        <div className="min-w-0 flex-1 overflow-x-auto">
          <div className="min-w-[320px]">
            <div className="mb-2 grid grid-cols-[1.5rem_repeat(4,minmax(0,1fr))] gap-2">
              <span />
              {SCORES.map((vulnerability) => (
                <span key={vulnerability} className="text-center text-xs text-muted-foreground">
                  V{vulnerability} ({LEVEL_LABELS[vulnerability]})
                </span>
              ))}
            </div>

            {EXPOSURE_ROWS.map((exposure) => (
              <div key={exposure} className="mb-2 grid grid-cols-[1.5rem_repeat(4,minmax(0,1fr))] items-center gap-2">
                <span className="text-center text-xs font-bold text-muted-foreground">{exposure}</span>
                {SCORES.map((vulnerability) => {
                  const risk = scoreRisk(exposure, vulnerability);
                  return (
                    <div
                      key={vulnerability}
                      className={cn(
                        "flex h-14 flex-col items-center justify-center rounded-lg border",
                        LEVEL_BADGE_CLASSES[risk],
                      )}
                      title={`Exposure ${exposure} × Vulnerability ${vulnerability} = ${LEVEL_LABELS[risk]}`}
                    >
                      <span className="text-base font-bold leading-6">{risk}</span>
                      <span className="text-[9px] font-bold leading-4 opacity-70">
                        E{exposure}/V{vulnerability}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}

            <p className="mt-1 text-center text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
              Vulnerability 1–4 →
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {SCORES.map((level) => (
          <span key={level} className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className={cn("size-3.5 rounded-sm border", LEVEL_BADGE_CLASSES[level])} />
            Level {level} ({LEVEL_LABELS[level]})
          </span>
        ))}
      </div>
    </div>
  );
}
